import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ConvexReactClient } from "convex/react";
import { App } from "./app";
import "./output.css";

interface RuntimeEnv {
  VITE_CONVEX_URL?: string;
  VITE_CLERK_PUBLISHABLE_KEY?: string;
}

function getImportMetaEnv(): RuntimeEnv {
  const env = (import.meta as ImportMeta & { env?: RuntimeEnv }).env;
  return env ?? {};
}

async function getRuntimeEnv(): Promise<RuntimeEnv> {
  const importMetaEnv = getImportMetaEnv();

  if (importMetaEnv.VITE_CONVEX_URL && importMetaEnv.VITE_CLERK_PUBLISHABLE_KEY) {
    return importMetaEnv;
  }

  try {
    const response = await fetch("/api/env", { cache: "no-store" });
    if (!response.ok) return importMetaEnv;
    const serverEnv = (await response.json()) as RuntimeEnv;
    return {
      VITE_CONVEX_URL: importMetaEnv.VITE_CONVEX_URL ?? serverEnv.VITE_CONVEX_URL,
      VITE_CLERK_PUBLISHABLE_KEY:
        importMetaEnv.VITE_CLERK_PUBLISHABLE_KEY ?? serverEnv.VITE_CLERK_PUBLISHABLE_KEY,
    };
  } catch {
    return importMetaEnv;
  }
}

async function bootstrap() {
  const env = await getRuntimeEnv();
  const convexUrl = env.VITE_CONVEX_URL;
  const clerkPublishableKey = env.VITE_CLERK_PUBLISHABLE_KEY;

  if (!clerkPublishableKey) {
    throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY environment variable");
  }
  if (!convexUrl) {
    throw new Error("Missing VITE_CONVEX_URL environment variable");
  }

  const convex = new ConvexReactClient(convexUrl);

  const root = document.getElementById("root");
  if (!root) throw new Error("Root element not found");

  createRoot(root).render(
    <StrictMode>
      <ClerkProvider publishableKey={clerkPublishableKey} afterSignOutUrl="/">
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          <App />
        </ConvexProviderWithClerk>
      </ClerkProvider>
    </StrictMode>,
  );
}

void bootstrap();
